import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Loader } from "semantic-ui-react";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { GridPager } from "../../common/pagination/GridPager";
import PayrollDataGrid, { payrollActionsCellClass, payrollActionsHeaderClass } from "./PayrollDataGrid";
import PayrollModal from "./PayrollModal";
import SalaryAssignmentFilters from "./SalaryAssignmentFilters";
import {
  computeGrossBounds,
  createFullRangeGrossFilter,
  downloadSalaryAssignmentsCsv,
  downloadSalaryAssignmentsExcel,
  downloadSalaryAssignmentsPdf,
  filterSalaryAssignmentRows,
} from "./salaryAssignmentFiltersUtils";
import {
  fetchTimesheetProjects,
  mapTimesheetProjectsResponse,
} from "../timesheets/timesheetApi";
import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { Common } from "../../../utils";
import "../documents/Documents.css";
import "../timesheets/TimeSheets.css";
import "./Payroll.css";

const PAGE_SIZE = 10;
const { PayrollStructure, UserSalaryAssignment, Department, Office } = config.REST_API;

/**
 * Lines saved from Payroll structures UI use AmountType: 0 = % of gross, 1 = fixed
 * (see PayrollStructuresPage UNIT_TO_ENUM / detail row mapping — differs from Core enum names).
 */
const StructureLineAmount = { PercentOfGrossUi: 0, FixedAmountUi: 1 };
/** Backend CustomEnums.SalaryComponentKind */
const ComponentKind = {
  Earning: 0,
  Deduction: 1,
  StatutoryDeduction: 2,
  EmployerContribution: 3,
  Other: 4,
};

function formatGrossDisplay(grossSalary, currencyCode) {
  if (grossSalary == null || grossSalary === "") return "";
  const n = typeof grossSalary === "number" ? grossSalary : Number(grossSalary);
  if (Number.isNaN(n)) return "";
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
  return currencyCode ? `${formatted} ${currencyCode}` : formatted;
}

function formatMoneyOne(n) {
  if (!Number.isFinite(n)) return "0";
  return new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(n);
}

function unwrapList(res) {
  const body = res?.data;
  if (body == null || body.success === false) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  return [];
}

function unwrapOne(res) {
  const body = res?.data;
  if (body == null || body.success === false) return null;
  if (body.data && typeof body.data === "object") return body.data;
  if (typeof body === "object" && !Array.isArray(body)) return body;
  return null;
}

function resolveStructureId(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const keys = ["id", "Id", "payrollStructureId", "PayrollStructureId"];
  for (const k of keys) {
    const v = r[k];
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (s && s !== "null" && s !== "undefined") return s;
  }
  return "";
}

function normalizeStructureOption(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const get = (a, b) => (r[a] !== undefined && r[a] !== null ? r[a] : r[b]);
  const statusVal = Number(get("status", "Status") ?? 0);
  return {
    id: resolveStructureId(r),
    name: String(get("name", "Name") ?? ""),
    status: statusVal,
  };
}

function structureLineIsPercentOfGross(amountType) {
  return Number(amountType) === StructureLineAmount.PercentOfGrossUi;
}

function computeLineAmount(gross, amountType, amountValue) {
  const g = Number(gross);
  const v = Number(amountValue);
  if (!Number.isFinite(g) || !Number.isFinite(v)) return 0;
  if (structureLineIsPercentOfGross(amountType)) return (g * v) / 100;
  return v;
}

function normalizeComponentKind(raw) {
  if (raw === null || raw === undefined) return ComponentKind.Earning;
  if (typeof raw === "string") {
    const s = raw.trim();
    const byName = {
      Earning: ComponentKind.Earning,
      Deduction: ComponentKind.Deduction,
      StatutoryDeduction: ComponentKind.StatutoryDeduction,
      EmployerContribution: ComponentKind.EmployerContribution,
      Other: ComponentKind.Other,
    };
    if (byName[s] !== undefined) return byName[s];
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : ComponentKind.Earning;
}

function computeBreakdown(gross, lines) {
  const g = Number(gross);
  const empty = { earnings: [], deductions: [], employer: [], net: 0, totalEarnings: 0, totalDeductions: 0 };
  if (!Number.isFinite(g) || g < 0 || !Array.isArray(lines)) return empty;

  const pushRow = (bucket, name, amountType, amountValue, amount) => {
    const pctLabel = structureLineIsPercentOfGross(amountType) ? ` (${amountValue}%)` : "";
    bucket.push({ name, pctLabel, amount });
  };

  const earnings = [];
  const deductions = [];
  const employer = [];

  for (const raw of lines) {
    const name = String(raw.salaryComponentName ?? raw.SalaryComponentName ?? "—");
    const amountType = Number(raw.amountType ?? raw.AmountType ?? 0);
    const amountValue = Number(raw.amountValue ?? raw.AmountValue ?? 0);
    const kind = normalizeComponentKind(raw.salaryComponentKind ?? raw.SalaryComponentKind);
    let amount = computeLineAmount(g, amountType, amountValue);
    if (!Number.isFinite(amount)) amount = 0;
    amount = Math.round(amount * 1000) / 1000;

    if (kind === ComponentKind.EmployerContribution) pushRow(employer, name, amountType, amountValue, amount);
    else if (
      kind === ComponentKind.Deduction ||
      kind === ComponentKind.StatutoryDeduction ||
      kind === ComponentKind.Other
    )
      pushRow(deductions, name, amountType, amountValue, amount);
    else pushRow(earnings, name, amountType, amountValue, amount);
  }

  const totalEarnings = earnings.reduce((s, r) => s + r.amount, 0);
  const totalDeductions = deductions.reduce((s, r) => s + r.amount, 0);
  const net = Math.round((g + totalEarnings - totalDeductions) * 1000) / 1000;
  return { earnings, deductions, employer, net, totalEarnings, totalDeductions };
}

function sanitizeGrossInput(value) {
  let v = String(value ?? "").replace(/[^0-9.]/g, "");
  const firstDot = v.indexOf(".");
  if (firstDot !== -1) {
    v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
  }
  return v;
}

function utcStartOfTodayIso() {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0)).toISOString();
}

const SalaryAssignmentsPage = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalRow, setModalRow] = useState(null);
  const [structureOptions, setStructureOptions] = useState([]);
  const [structureLines, setStructureLines] = useState([]);
  const [structureDetailLoading, setStructureDetailLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedStructureId, setSelectedStructureId] = useState("");
  const [grossStr, setGrossStr] = useState("");
  const [assignmentId, setAssignmentId] = useState("");
  const [saving, setSaving] = useState(false);

  const [employeeQuery, setEmployeeQuery] = useState("");
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [officeFilter, setOfficeFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [payrollStructureFilter, setPayrollStructureFilter] = useState("all");
  const [grossFilter, setGrossFilter] = useState(() => createFullRangeGrossFilter({ min: 0, max: 100000 }));
  const [exportLoading, setExportLoading] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [offices, setOffices] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [filterStructureOptions, setFilterStructureOptions] = useState([]);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedEmployeeSearch(employeeQuery.trim()), 350);
    return () => window.clearTimeout(id);
  }, [employeeQuery]);

  useEffect(() => {
    setPage(1);
  }, [debouncedEmployeeSearch, departmentFilter, officeFilter, projectFilter, payrollStructureFilter, grossFilter]);

  const loadGrid = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const url = `${config.ROOT_URL}${UserSalaryAssignment.CompanyGrid}`;
      const { data } = await axiosBasic.get(url);
      if (data && data.success && Array.isArray(data.data)) {
        const mapped = data.data.map((item) => ({
          userId: item.userId ?? item.UserId ?? "",
          employeeName: item.employeeName ?? item.EmployeeName ?? "",
          departmentName: item.departmentName ?? item.DepartmentName ?? "",
          workplaceName: item.workplaceName ?? item.WorkplaceName ?? "",
          payrollStructureId: item.payrollStructureId ?? item.PayrollStructureId ?? "",
          payrollStructureName: item.payrollStructureName ?? item.PayrollStructureName ?? "",
          grossSalary: item.grossSalary ?? item.GrossSalary,
          currencyCode: item.currencyCode ?? item.CurrencyCode ?? "",
          assignmentId: item.assignmentId ?? item.AssignmentId ?? "",
          departmentId: item.departmentId ?? item.DepartmentId ?? null,
          officeId: item.officeId ?? item.OfficeId ?? null,
          projectIds: Array.isArray(item.projectIds)
            ? item.projectIds
            : Array.isArray(item.ProjectIds)
              ? item.ProjectIds
              : [],
        }));
        setRows(mapped);
        setGrossFilter(createFullRangeGrossFilter(computeGrossBounds(mapped)));
        setPage(1);
      } else {
        setRows([]);
        setLoadError(data?.message || t("app.payroll.assignments.loadGridError"));
      }
    } catch (e) {
      setRows([]);
      setLoadError(e?.response?.data?.message || e?.message || t("app.payroll.assignments.loadGridError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadGrid();
  }, [loadGrid]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [depRes, offRes, structRes, projRaw] = await Promise.all([
          axiosBasic.get(Department.Get),
          axiosBasic.get(Office.GetCompanyOffices),
          axiosBasic.get(`${config.ROOT_URL}${UserSalaryAssignment.CompanyPayrollStructures}`),
          fetchTimesheetProjects(),
        ]);
        const depBody = depRes.data || {};
        if (depBody.success && Array.isArray(depBody.data)) {
          setDepartments(
            depBody.data
              .map((d) => ({ id: d.id, name: d.name }))
              .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }))
          );
        }
        const offBody = offRes.data || {};
        if (offBody.success && Array.isArray(offBody.data)) {
          setOffices(
            offBody.data
              .map((o) => ({
                id: o.id,
                name: o.countryName ? `${o.name} (${o.countryName})` : o.name,
              }))
              .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }))
          );
        }
        const structList = unwrapList(structRes).map(normalizeStructureOption);
        setFilterStructureOptions(
          structList
            .filter((s) => s.id && s.name)
            .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }))
        );
        setProjectOptions(mapTimesheetProjectsResponse(projRaw));
      } catch {
        setDepartments([]);
        setOffices([]);
        setFilterStructureOptions([]);
        setProjectOptions([]);
      }
    };
    loadLookups();
  }, []);

  const grossBounds = useMemo(() => computeGrossBounds(rows), [rows]);

  const filteredRows = useMemo(
    () =>
      filterSalaryAssignmentRows(rows, {
        employeeSearch: debouncedEmployeeSearch,
        departmentId: departmentFilter,
        officeId: officeFilter,
        projectId: projectFilter,
        payrollStructureId: payrollStructureFilter,
        grossFilter,
        grossBounds,
      }),
    [rows, debouncedEmployeeSearch, departmentFilter, officeFilter, projectFilter, payrollStructureFilter, grossFilter, grossBounds]
  );

  const clearFilters = () => {
    setEmployeeQuery("");
    setDebouncedEmployeeSearch("");
    setDepartmentFilter("all");
    setOfficeFilter("all");
    setProjectFilter("all");
    setPayrollStructureFilter("all");
    setGrossFilter(createFullRangeGrossFilter(grossBounds));
    setPage(1);
  };

  const runExport = (format) => {
    if (exportLoading) return;
    setExportLoading(format);
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      const base = `salary-assignments-${stamp}`;
      if (format === "excel") {
        downloadSalaryAssignmentsExcel(filteredRows, t, base);
        Common.showSuccessMessage(t("app.payroll.assignments.exportExcelSuccess"));
      } else if (format === "csv") {
        downloadSalaryAssignmentsCsv(filteredRows, t, base);
        Common.showSuccessMessage(t("app.payroll.assignments.exportCsvSuccess"));
      } else if (format === "pdf") {
        const ok = downloadSalaryAssignmentsPdf(filteredRows, t, base);
        if (!ok) Common.showErrorMessage(t("app.payroll.assignments.exportPdfBlocked"));
        else Common.showSuccessMessage(t("app.payroll.assignments.exportPdfSuccess"));
      }
    } finally {
      setExportLoading(null);
    }
  };

  useEffect(() => {
    if (!modalOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosBasic.get(`${config.ROOT_URL}${UserSalaryAssignment.CompanyPayrollStructures}`);
        const list = unwrapList(res).map(normalizeStructureOption);
        if (!cancelled) setStructureOptions(list);
      } catch {
        if (!cancelled) setStructureOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen || !selectedStructureId) {
      setStructureLines([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setStructureDetailLoading(true);
      try {
        const res = await axiosBasic.get(
          `${config.ROOT_URL}${PayrollStructure.DetailBase}${encodeURIComponent(selectedStructureId)}`
        );
        const d = unwrapOne(res);
        const rawLines = d?.lines ?? d?.Lines ?? [];
        if (!cancelled) setStructureLines(Array.isArray(rawLines) ? rawLines : []);
      } catch {
        if (!cancelled) {
          setStructureLines([]);
          Common.showErrorMessage(t("app.payroll.assignments.structureLoadError"));
        }
      } finally {
        if (!cancelled) setStructureDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modalOpen, selectedStructureId, t]);

  const grossNum = useMemo(() => {
    const n = parseFloat(String(grossStr).trim());
    return Number.isFinite(n) && n >= 0 ? n : NaN;
  }, [grossStr]);

  const showCalculationSummary =
    grossStr.trim() !== "" &&
    Number.isFinite(grossNum) &&
    Boolean(selectedStructureId) &&
    structureLines.length > 0 &&
    !structureDetailLoading;

  const breakdown = useMemo(() => {
    if (!showCalculationSummary) {
      return { earnings: [], deductions: [], employer: [], net: 0, totalEarnings: 0, totalDeductions: 0 };
    }
    return computeBreakdown(grossNum, structureLines);
  }, [showCalculationSummary, grossNum, structureLines]);

  const selectedStructureMeta = useMemo(
    () => structureOptions.find((s) => s.id === selectedStructureId),
    [structureOptions, selectedStructureId]
  );
  const isSelectedStructurePublished = Boolean(selectedStructureMeta && selectedStructureMeta.status === 1);

  const openModalForRow = (row) => {
    if (!row) return;
    setModalRow(row);
    setSelectedUserId(row.userId || "");
    setSelectedStructureId(row.payrollStructureId || "");
    setGrossStr(row.grossSalary != null && row.grossSalary !== "" ? String(row.grossSalary) : "");
    setAssignmentId(row.assignmentId || "");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalRow(null);
    setStructureLines([]);
    setStructureOptions([]);
    setSelectedUserId("");
    setSelectedStructureId("");
    setGrossStr("");
    setAssignmentId("");
  };

  const canSubmit =
    Boolean(selectedUserId) &&
    Boolean(selectedStructureId) &&
    isSelectedStructurePublished &&
    Number.isFinite(grossNum) &&
    grossNum > 0 &&
    !structureDetailLoading &&
    !saving;

  const handleAssignSalary = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const effectiveFrom = utcStartOfTodayIso();
      const selectedEmp = rows.find((x) => x.userId === selectedUserId);
      const deptId = selectedEmp?.departmentId ?? null;
      const offId = selectedEmp?.officeId ?? null;
      const payload = {
        UserId: selectedUserId,
        PayrollStructureId: selectedStructureId,
        SalaryBasis: 0,
        GrossSalary: grossNum,
        EffectiveFrom: effectiveFrom,
        EffectiveTo: null,
        DepartmentId: deptId,
        OfficeId: offId,
      };
      let res;
      if (assignmentId) {
        res = await axiosBasic.patch(`${config.ROOT_URL}${UserSalaryAssignment.Patch}`, {
          ...payload,
          Id: assignmentId,
        });
      } else {
        res = await axiosBasic.post(`${config.ROOT_URL}${UserSalaryAssignment.Create}`, payload);
      }
      const body = res?.data;
      if (body && body.success) {
        Common.showSuccessMessage(t("app.payroll.assignments.assignSuccess"));
        closeModal();
        await loadGrid();
      } else {
        Common.showErrorMessage(body?.message || t("app.payroll.assignments.assignError"));
      }
    } catch (e) {
      Common.showErrorMessage(e?.response?.data?.message || e?.message || t("app.payroll.assignments.assignError"));
    } finally {
      setSaving(false);
    }
  };

  const total = filteredRows.length;
  const slice = useMemo(
    () => filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredRows, page]
  );

  const columns = useMemo(
    () => [
      { key: "employee", label: t("app.payroll.assignments.colEmployee"), sortable: false },
      { key: "department", label: t("app.payroll.assignments.colDepartment"), sortable: false },
      { key: "office", label: t("app.payroll.assignments.colOffice"), sortable: false },
      { key: "structure", label: t("app.payroll.assignments.colStructure"), sortable: false },
      { key: "gross", label: t("app.payroll.assignments.colGross"), sortable: false },
      { key: "actions", label: t("app.payroll.common.actions"), sortable: false, headerClassName: payrollActionsHeaderClass },
    ],
    [t]
  );

  const modalSubtitle = modalRow?.employeeName ?? "";

  return (
    <div className="documents-page payroll-page ts-page px-3 px-md-4 py-4">
      <CommonPageHeader
        icon={<i className="bi bi-person-badge" />}
        title={t("app.payroll.assignments.title")}
        description={t("app.payroll.assignments.subtitle")}
      />

      <Grid stackable className="mb-3">
        <Grid.Row>
          <Grid.Column width={16}>
            <section className="ts-appr-stats">
              <div className="ts-appr-stat-card ts-appr-stat-card--total">
                <div className="ts-appr-stat-card__accent" aria-hidden />
                <div className="ts-appr-stat-card__inner">
                  <div className="ts-appr-stat-card__top">
                    <span className="ts-appr-stat-card__icon">
                      <i className="bi bi-people" />
                    </span>
                    <div className="ts-appr-stat-card__figures">
                      <span className="ts-appr-stat-card__value">{loading ? "—" : total}</span>
                      <span className="ts-appr-stat-card__label">{t("app.payroll.assignments.statAssigned")}</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <SalaryAssignmentFilters
        employeeQuery={employeeQuery}
        onEmployeeQueryChange={setEmployeeQuery}
        departmentFilter={departmentFilter}
        onDepartmentFilterChange={setDepartmentFilter}
        officeFilter={officeFilter}
        onOfficeFilterChange={setOfficeFilter}
        projectFilter={projectFilter}
        onProjectFilterChange={setProjectFilter}
        payrollStructureFilter={payrollStructureFilter}
        onPayrollStructureFilterChange={setPayrollStructureFilter}
        grossFilter={grossFilter}
        onGrossFilterChange={setGrossFilter}
        grossBounds={grossBounds}
        departments={departments}
        offices={offices}
        projects={projectOptions}
        payrollStructures={filterStructureOptions}
        exportLoading={exportLoading}
        onExportExcel={() => runExport("excel")}
        onExportCsv={() => runExport("csv")}
        onExportPdf={() => runExport("pdf")}
        onClearFilters={clearFilters}
        listLoading={loading}
      />

      <p className="documents-page-description ts-appr-results-hint mb-3">
        {t("app.payroll.assignments.resultsHint", {
          shown: slice.length,
          total,
        })}
      </p>

      {loadError && (
        <div className="alert alert-warning d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3" role="alert">
          <span>{loadError}</span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadGrid}>
            {t("app.payroll.common.retry")}
          </button>
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Loader active inline="centered">
            {t("app.payroll.assignments.loadingGrid")}
          </Loader>
        </div>
      ) : (
        <>
          <PayrollDataGrid
            columns={columns}
            rows={slice}
            getRowKey={(row) => String(row.userId)}
            emptyMessage={t("app.payroll.common.noResults")}
            renderRow={(row) => {
              const name = (row.payrollStructureName || "").trim();
              const id = (row.payrollStructureId || "").trim();
              const structureCell = name || id || "";
              const structureTitle = id && name !== id ? id : undefined;
              const grossCell = formatGrossDisplay(row.grossSalary, row.currencyCode);
              return (
                <>
                  <td className="fw-semibold">{row.employeeName || ""}</td>
                  <td>{row.departmentName || ""}</td>
                  <td>{row.workplaceName || ""}</td>
                  <td title={structureTitle || undefined}>{structureCell}</td>
                  <td>{grossCell}</td>
                  <td className={payrollActionsCellClass}>
                    <button
                      type="button"
                      className="documents-grid-action-btn documents-grid-action-btn--edit documents-action-compact"
                      onClick={() => openModalForRow(row)}
                    >
                      {t("app.payroll.common.edit")}
                    </button>
                  </td>
                </>
              );
            }}
          />

          <div className="documents-pagination documents-pagination--grid-pager d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2">
            <span className="documents-pagination-total text-muted small">
              {total === 0
                ? t("app.payroll.common.noResults")
                : t("app.payroll.common.showingRange", {
                    from: (page - 1) * PAGE_SIZE + 1,
                    to: Math.min(page * PAGE_SIZE, total),
                    total,
                  })}
            </span>
            <GridPager
              className="documents-rc-pagination"
              onChange={(p) => setPage(p)}
              current={page}
              total={total}
              pageSize={PAGE_SIZE}
              showLessItems
              showTitle={false}
              showSizeChanger={false}
            />
          </div>
        </>
      )}

      <PayrollModal
        open={modalOpen}
        onClose={closeModal}
        title={t("app.payroll.assignments.modalTitle")}
        subtitle={modalSubtitle}
        titleId="payroll-assignment-modal-title"
        size="large"
        variant="salary"
        headerIcon={<i className="bi bi-calculator" aria-hidden />}
        footer={
          <>
            <button type="button" className="att-btn att-btn--ghost" onClick={closeModal} disabled={saving}>
              {t("app.payroll.common.cancel")}
            </button>
            <button
              type="button"
              className="payroll-assign-footer__assign att-btn"
              onClick={handleAssignSalary}
              disabled={!canSubmit}
            >
              {saving ? t("app.payroll.assignments.assignSaving") : t("app.payroll.assignments.assignSalary")}
            </button>
          </>
        }
      >
        <div className="d-flex flex-column gap-3">
          <div>
            <span className="att-modal-field-label d-block">{t("app.payroll.assignments.employeeReadOnly")}</span>
            <div className="form-control bg-light" id="payroll-as-emp-ro">
              {modalRow?.employeeName ?? "—"}
            </div>
          </div>

          <div>
            <label className="att-modal-field-label" htmlFor="payroll-as-structure">
              {t("app.payroll.assignments.fieldStructure")}
            </label>
            <select
              id="payroll-as-structure"
              className="form-select"
              value={selectedStructureId}
              onChange={(e) => setSelectedStructureId(e.target.value)}
            >
              <option value="">{t("app.payroll.assignments.pickStructure")}</option>
              {structureOptions.length === 0 ? (
                <option value="" disabled>
                  {t("app.payroll.assignments.noStructuresInCompany")}
                </option>
              ) : (
                structureOptions.map((s) => {
                  const published = s.status === 1;
                  const suffix = published
                    ? ""
                    : s.status === 2
                      ? ` ${t("app.payroll.assignments.structureArchivedSuffix")}`
                      : ` ${t("app.payroll.assignments.structureDraftSuffix")}`;
                  return (
                    <option key={s.id} value={s.id} disabled={!published}>
                      {s.name}
                      {suffix}
                    </option>
                  );
                })
              )}
            </select>
            {structureOptions.length > 0 && !structureOptions.some((s) => s.status === 1) ? (
              <p className="text-muted small mt-1 mb-0">{t("app.payroll.assignments.noPublishedStructures")}</p>
            ) : null}
          </div>

          <div>
            <label className="att-modal-field-label" htmlFor="payroll-as-gross">
              {t("app.payroll.assignments.fieldGross")}
            </label>
            <input
              id="payroll-as-gross"
              type="text"
              inputMode="decimal"
              className="form-control"
              placeholder="0"
              autoComplete="off"
              value={grossStr}
              onChange={(e) => setGrossStr(sanitizeGrossInput(e.target.value))}
            />
          </div>

          {selectedStructureId && structureDetailLoading ? (
            <div className="text-muted small py-2 d-flex align-items-center gap-2">
              <Loader active inline="centered" />
              <span>{t("app.payroll.assignments.loadingStructureDetail")}</span>
            </div>
          ) : null}

          {showCalculationSummary ? (
            <div>
              <div className="att-modal-field-label mb-2">{t("app.payroll.assignments.calculationSummary")}</div>
              <div className="payroll-assign-summary">
                <div className="payroll-assign-summary__title">{t("app.payroll.assignments.sectionEarnings")}</div>
                {breakdown.earnings.length === 0 ? (
                  <div className="payroll-assign-summary__row text-muted small">{t("app.payroll.common.noResults")}</div>
                ) : (
                  breakdown.earnings.map((r, i) => (
                    <div key={`e-${i}`} className="payroll-assign-summary__row">
                      <span className="payroll-assign-summary__row-name">
                        {r.name}
                        {r.pctLabel}
                      </span>
                      <span className="payroll-assign-summary__row-val">{formatMoneyOne(r.amount)}</span>
                    </div>
                  ))
                )}

                <div className="payroll-assign-summary__title">{t("app.payroll.assignments.sectionDeductions")}</div>
                {breakdown.deductions.length === 0 ? (
                  <div className="payroll-assign-summary__row text-muted small py-2">—</div>
                ) : (
                  breakdown.deductions.map((r, i) => (
                    <div key={`d-${i}`} className="payroll-assign-summary__row">
                      <span className="payroll-assign-summary__row-name">
                        {r.name}
                        {r.pctLabel}
                      </span>
                      <span className="payroll-assign-summary__row-val">{formatMoneyOne(r.amount)}</span>
                    </div>
                  ))
                )}

                {breakdown.employer.length > 0 ? (
                  <>
                    <div className="payroll-assign-summary__title">{t("app.payroll.assignments.sectionEmployer")}</div>
                    {breakdown.employer.map((r, i) => (
                      <div key={`m-${i}`} className="payroll-assign-summary__row">
                        <span className="payroll-assign-summary__row-name">
                          {r.name}
                          {r.pctLabel}
                        </span>
                        <span className="payroll-assign-summary__row-val">{formatMoneyOne(r.amount)}</span>
                      </div>
                    ))}
                  </>
                ) : null}

                <div className="payroll-assign-summary__net-row">
                  <span className="payroll-assign-summary__net-label">{t("app.payroll.assignments.netSalary")}</span>
                  <span className="payroll-assign-summary__net-val">{formatMoneyOne(breakdown.net)}</span>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </PayrollModal>
    </div>
  );
};

export default SalaryAssignmentsPage;
